import ToggleRow from '@components/Customization/ToggleRow';
import { useCustomizationLocales } from '@lib/customization/useCustomizationLocales';
import { useLocalization } from '@lib/localization';
import {
  Box, Button, Chip, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputAdornment,
  InputLabel, LinearProgress, MenuItem, OutlinedInput, Select, Slider, Stack, TextField, Typography,
} from '@mui/material';
import {
  countPillStyles, previewFrameStyles, previewGroupHeaderStyles, previewRangeStyles,
} from '@pages/CustomPoiTypes.styles';
import { resolveLabel, type PoiField, type ResolvedItem } from '@store/api/poiTypeEndpoints';
import { useEffect, useState } from 'react';
import { AngleDownIcon, BarcodeIcon } from 'react-line-awesome';

/** Whatever a field holds while the preview is open. Same shape as `PoiField['value']`. */
type PreviewValue = string | number | boolean | string[];

interface Props {
  open: boolean;
  /** The resolved schema, inherited content included, in the order a device renders it. */
  items: ResolvedItem[] | undefined;
  typeName: string;
  loading: boolean;
  onClose: () => void;
}

/** Addressed by field id; an unsaved field has none yet, so its position stands in. */
const keyOf = (field: PoiField, fallback: string) => field.id ?? fallback;

/**
 * What a field starts the preview holding: the author's default, or the empty answer for
 * its type. `value` is the same key an inspector's answer is written over, so seeding from
 * it is exactly what a device does when it opens the form.
 */
const emptyFor = (field: PoiField): PreviewValue => {
  switch (field.type) {
    case 'Toggle':
      return false;
    case 'Number':
    case 'Range':
      return field.min ?? 0;
    case 'MultiList':
      return [];
    default:
      return '';
  }
};

const seedValues = (items: ResolvedItem[]): Record<string, PreviewValue> => {
  const seeded: Record<string, PreviewValue> = {};

  items.forEach((entry, index) => {
    const fields = entry.kind === 'field' ? [entry.field] : entry.group.fields;

    fields.forEach((field, fieldIndex) => {
      seeded[keyOf(field, `${index}-${fieldIndex}`)] = field.value ?? emptyFor(field);
    });
  });

  return seeded;
};

/**
 * The type as an inspector meets it, rather than as its author edits it.
 *
 * Rendered straight from the resolved `items` in the order the API returns them, so what
 * this shows is what a device draws — inherited content included, and in the same place.
 * Nothing is saved: the controls are live only so the layout can be judged with a field
 * actually filled in.
 *
 * Built in the field drawer's vocabulary — small outlined inputs carrying their own
 * floating label, `ToggleRow` for a switch, a captioned frame for what MUI leaves
 * unbordered — so previewing a type and authoring one do not look like two products.
 *
 * Groups are a heading and a collapse, nothing more: their fields sit at the same width as
 * the ungrouped ones, because a card inside the preview's own frame is a box in a box.
 */
const PoiTypePreview: React.FC<Props> = ({ open, items, typeName, loading, onClose }) => {
  const { t } = useLocalization();
  const locales = useCustomizationLocales();

  const [values, setValues] = useState<Record<string, PreviewValue>>({});
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  // Reseed whenever the dialog opens or the schema behind it changes, so an edit made
  // after a preview is not shown with the previous run's answers still in the boxes.
  useEffect(() => {
    if (open && items) {
      setValues(seedValues(items));
      setCollapsed(new Set());
    }
  }, [open, items]);

  const label = (field: PoiField) => resolveLabel(field.label, locales);

  const toggleGroup = (key: string) => setCollapsed((current) => {
    const next = new Set(current);
    if (!next.delete(key)) next.add(key);
    return next;
  });

  const renderField = (field: PoiField, key: string) => {
    const value = values[key];
    const set = (next: PreviewValue) => setValues((current) => ({ ...current, [key]: next }));

    // What every text-shaped control shares with the drawer's inputs: small, full width,
    // its own floating label, the author's help text underneath.
    const input = {
      key,
      fullWidth: true,
      size: 'small' as const,
      required: field.required,
      disabled: field.readOnly,
      label: label(field),
      helperText: field.help,
    };

    switch (field.type) {
      case 'Toggle':
        return (
          <ToggleRow
            key={key}
            label={`${label(field)}${field.required ? ' *' : ''}`}
            description={field.help}
            checked={Boolean(value)}
            disabled={field.readOnly}
            onChange={(checked) => set(checked)}
          />
        );

      case 'Range':
        return (
          <Box key={key} sx={previewRangeStyles}>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={2}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {label(field)}
                {field.required && <Box component="span" sx={{ color: 'error.main' }}> *</Box>}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{String(value)}</Typography>
            </Stack>

            {field.help && (
              <Typography variant="caption" color="text.secondary">{field.help}</Typography>
            )}

            <Slider
              size="small"
              value={Number(value)}
              min={field.min ?? 0}
              max={field.max ?? 100}
              step={field.decimal ? 0.1 : 1}
              disabled={field.readOnly}
              valueLabelDisplay="auto"
              onChange={(_, next) => set(next as number)}
              sx={{ mt: 0.5 }}
            />
          </Box>
        );

      case 'List':
        return (
          <TextField
            {...input}
            select
            value={String(value ?? '')}
            onChange={(event) => set(event.target.value)}
          >
            {(field.options ?? []).map((option, index) => (
              <MenuItem key={option.id ?? index} value={option.id ?? ''}>
                {resolveLabel(option.label, locales)}
              </MenuItem>
            ))}
          </TextField>
        );

      case 'MultiList': {
        const selected = Array.isArray(value) ? value : [];
        const nameOf = (id: string) => {
          const option = (field.options ?? []).find((candidate) => candidate.id === id);

          return option ? resolveLabel(option.label, locales) : id;
        };

        return (
          <FormControl key={key} fullWidth size="small" required={field.required} disabled={field.readOnly}>
            <InputLabel>{label(field)}</InputLabel>
            <Select
              multiple
              value={selected}
              input={<OutlinedInput label={label(field)} />}
              onChange={(event) => set(
                typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value
              )}
              renderValue={(ids) => (
                <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                  {ids.map((id) => <Chip key={id} size="small" label={nameOf(id)} />)}
                </Stack>
              )}
            >
              {(field.options ?? []).map((option, index) => (
                <MenuItem key={option.id ?? index} value={option.id ?? ''}>
                  {resolveLabel(option.label, locales)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      }

      case 'Number':
        return (
          <TextField
            {...input}
            type="number"
            value={String(value ?? '')}
            placeholder={field.placeholder}
            slotProps={{ htmlInput: { step: field.decimal ? 'any' : 1 } }}
            onChange={(event) => set(event.target.value)}
          />
        );

      case 'Barcode':
        return (
          <TextField
            {...input}
            value={String(value ?? '')}
            placeholder={field.placeholder}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <Box sx={{ color: 'text.disabled', display: 'inline-flex', fontSize: 18 }}>
                      <BarcodeIcon />
                    </Box>
                  </InputAdornment>
                ),
              },
            }}
            onChange={(event) => set(event.target.value)}
          />
        );

      default:
        return (
          <TextField
            {...input}
            value={String(value ?? '')}
            placeholder={field.placeholder}
            multiline={field.multiline}
            minRows={field.multiline ? 3 : undefined}
            slotProps={{ htmlInput: { maxLength: field.charLimit } }}
            onChange={(event) => set(event.target.value)}
          />
        );
    }
  };

  const body = (items ?? []).map((entry, index) => {
    if (entry.kind === 'field') {
      return renderField(entry.field, keyOf(entry.field, String(index)));
    }

    const groupKey = entry.group.id ?? `g-${index}`;
    const isCollapsed = collapsed.has(groupKey);

    // A plain wrapper: it carries the collapse and nothing visual, so the fields inside
    // keep the same left edge and width as the ungrouped fields around it.
    return (
      <Box key={groupKey}>
        <Stack direction="row" sx={previewGroupHeaderStyles} onClick={() => toggleGroup(groupKey)}>
          <Box
            sx={{
              color: 'text.disabled',
              display: 'inline-flex',
              fontSize: 16,
              transition: 'transform 150ms ease',
              transform: isCollapsed ? 'rotate(-90deg)' : 'none',
            }}
          >
            <AngleDownIcon />
          </Box>
          <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, lineHeight: 1 }}>
            {resolveLabel(entry.group.name, locales)}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Box component="span" sx={countPillStyles}>{entry.group.fields.length}</Box>
        </Stack>

        {entry.group.description && !isCollapsed && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {entry.group.description}
          </Typography>
        )}

        <Collapse in={!isCollapsed}>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            {entry.group.fields.map((field, fieldIndex) => renderField(
              field, keyOf(field, `${index}-${fieldIndex}`),
            ))}
          </Stack>
        </Collapse>
      </Box>
    );
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        {typeName}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t('Customization_PreviewSubtitle')}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {!loading && body.length === 0 && (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            {t('Customization_PreviewEmpty')}
          </Typography>
        )}

        {body.length > 0 && (
          <Box sx={previewFrameStyles}>
            <Stack spacing={2.5}>{body}</Stack>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{t('Common_Close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PoiTypePreview;
